import React, { useState, useEffect } from "react";
import { useProjects } from "../context/ProjectContext";
import { useAuth } from "../context/AuthContext";
import api, { apiError, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Loader2, Archive, Download, CheckCircle2, Plug, RefreshCw, Users2, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Projects() {
  const { projects, refresh, select } = useProjects();
  const { can } = useAuth();
  const empty = { project_number: "", project_name: "", status: "Active", start_date: "", reporting_date: "", procore_project_id: null, procore_project_name: null, manager_ids: [], labor_manager_ids: [] };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pms, setPms] = useState([]);
  const [procoreProjects, setProcoreProjects] = useState(null);
  const [procoreLoading, setProcoreLoading] = useState(false);
  const [procoreError, setProcoreError] = useState(null);
  const [procoreStatus, setProcoreStatus] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [syncAll, setSyncAll] = useState(null);

  useEffect(() => { api.get("/users/assignable").then((r) => setPms(r.data)).catch(() => setPms([])); }, []);
  const pmName = (id) => (pms.find((u) => u.id === id) || {}).name || "Unknown";
  const pmUsers = pms.filter((u) => u.role === "pm");
  const lmUsers = pms.filter((u) => u.role === "labor_manager");

  useEffect(() => {
    if (open && procoreProjects === null && !procoreLoading) {
      setProcoreLoading(true);
      setProcoreError(null);
      api.get("/procore/status").then((r) => setProcoreStatus(r.data)).catch(() => setProcoreStatus(null));
      api.get("/procore/projects")
        .then((r) => setProcoreProjects(r.data))
        .catch((e) => { setProcoreError(apiError(e.response?.data?.detail)); setProcoreProjects([]); })
        .finally(() => setProcoreLoading(false));
    }
  }, [open, procoreProjects, procoreLoading]);

  useEffect(() => {
    if (!procoreProjects || procoreProjects.length === 0 || !form.project_name || editingId) return;
    if (form.procore_project_id) return;
    const match = procoreProjects.find(
      (p) => (p.name || "").trim().toLowerCase() === form.project_name.trim().toLowerCase());
    if (match) setForm((f) => ({ ...f, procore_project_id: match.id, procore_project_name: match.name }));
  }, [procoreProjects, form.project_name, form.procore_project_id, editingId]);

  const openNew = () => { setEditingId(null); setForm(empty); setProcoreProjects(null); setProcoreError(null); setProcoreStatus(null); setOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      project_number: p.project_number, project_name: p.project_name, status: p.status || "Active",
      start_date: p.start_date || "", reporting_date: p.reporting_date || "",
      procore_project_id: p.procore_project_id || null, procore_project_name: p.procore_project_name || null,
      manager_ids: p.manager_ids || [], labor_manager_ids: p.labor_manager_ids || [],
    });
    setProcoreProjects(null); setProcoreError(null); setProcoreStatus(null);
    setOpen(true);
  };

  const togglePm = (id) => setForm((f) => ({
    ...f,
    manager_ids: f.manager_ids.includes(id) ? f.manager_ids.filter((x) => x !== id) : [...f.manager_ids, id],
  }));

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/projects/${editingId}`, form);
        toast.success("Project updated");
        setOpen(false); await refresh();
      } else {
        const { data } = await api.post("/projects", form);
        toast.success("Project created");
        setOpen(false); setForm(empty); setProcoreProjects(null); await refresh(); select(data.id);
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const archive = async (id) => {
    try { const { data } = await api.post(`/projects/${id}/archive`); toast.success(data.archived ? "Archived" : "Restored"); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const syncProcore = async (id) => {
    setSyncing(id);
    try {
      const { data } = await api.post(`/projects/${id}/procore/sync-budget`);
      toast.success(`Synced ${data.lines_synced} cost code line${data.lines_synced === 1 ? "" : "s"} from Procore (${data.rows_fetched} raw rows)`);
      await refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSyncing(null); }
  };
  const syncAllProjects = async () => {
    const linked = projects.filter((p) => p.procore_project_id && !p.archived);
    if (linked.length === 0) { toast.error("No projects are linked to Procore yet"); return; }
    const failed = [];
    for (let i = 0; i < linked.length; i++) {
      const p = linked[i];
      setSyncAll({ done: i, total: linked.length });
      setSyncing(p.id);
      try { await api.post(`/projects/${p.id}/procore/sync-budget`); }
      catch (e) { failed.push(`${p.project_number}: ${apiError(e.response?.data?.detail)}`); }
    }
    setSyncing(null); setSyncAll(null);
    await refresh();
    const ok = linked.length - failed.length;
    const skipped = projects.filter((p) => !p.procore_project_id && !p.archived).length;
    const note = skipped ? ` (${skipped} not linked to Procore, skipped)` : "";
    if (failed.length) toast.error(`Synced ${ok} of ${linked.length} projects${note}. Failed — ${failed.join("; ")}`);
    else toast.success(`Synced all ${ok} project${ok === 1 ? "" : "s"} from Procore${note}`);
  };

  const PmMultiSelect = () => (
    <div className="col-span-2">
      <Label className="text-xs flex items-center gap-1"><Users2 className="w-3 h-3 text-[#E8791A]" /> Project Managers</Label>
      <div className="mt-1 border border-slate-200 rounded-md p-2 max-h-36 overflow-y-auto space-y-1" data-testid="proj-pm-multiselect">
        {pmUsers.length === 0 ? (
          <div className="text-xs text-slate-400">No active PM users. Create PM users first.</div>
        ) : pmUsers.map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
            <input type="checkbox" checked={form.manager_ids.includes(u.id)} onChange={() => togglePm(u.id)}
                   data-testid={`proj-pm-option-${u.id}`} />
            {u.name} <span className="text-xs text-slate-400">{u.email}</span>
          </label>
        ))}
      </div>
      {form.manager_ids.length === 0 && <div className="text-[11px] text-amber-700 mt-1">No PM assigned — this project will be flagged as needing a PM.</div>}
    </div>
  );

  const toggleLm = (id) => setForm((f) => ({
    ...f,
    labor_manager_ids: f.labor_manager_ids.includes(id) ? f.labor_manager_ids.filter((x) => x !== id) : [...f.labor_manager_ids, id],
  }));

  const LaborManagerSelect = () => (
    <div className="col-span-2">
      <Label className="text-xs flex items-center gap-1"><Users2 className="w-3 h-3 text-[#E8791A]" /> Labor Managers</Label>
      <div className="mt-1 border border-slate-200 rounded-md p-2 max-h-36 overflow-y-auto space-y-1" data-testid="proj-lm-multiselect">
        {lmUsers.length === 0 ? (
          <div className="text-xs text-slate-400">No active Labor Manager users. Create one on the Users page.</div>
        ) : lmUsers.map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
            <input type="checkbox" checked={form.labor_manager_ids.includes(u.id)} onChange={() => toggleLm(u.id)}
                   data-testid={`proj-lm-option-${u.id}`} />
            {u.name} <span className="text-xs text-slate-400">{u.email}</span>
          </label>
        ))}
      </div>
      {form.labor_manager_ids.length === 0 && <div className="text-[11px] text-amber-700 mt-1">No Labor Manager assigned — this project will be flagged as needing one.</div>}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-bold text-slate-900">Projects</h1>
        <div className="flex gap-2">
          {can.manage && (
            <Button variant="outline" onClick={syncAllProjects} disabled={!!syncAll || !!syncing} data-testid="sync-all-projects-button"
                    className="hover:bg-[#E8791A] hover:text-white hover:border-[#E8791A]">
              {syncAll ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              {syncAll ? `Syncing ${syncAll.done + 1} of ${syncAll.total}…` : "Sync All"}
            </Button>
          )}
          {can.admin && (
            <Button onClick={openNew} className="bg-[#E8791A] hover:bg-[#D66A0F] text-white" data-testid="new-project-button"><Plus className="w-4 h-4 mr-1" />New Project</Button>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">{editingId ? "Edit Project" : "Create Project"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Project Number *</Label><Input value={form.project_number} onChange={(e) => setForm({ ...form, project_number: e.target.value })} data-testid="proj-number-input" className="mt-1" /></div>
            <div><Label className="text-xs">Status</Label><Input value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="proj-status-input" className="mt-1" /></div>
            <div className="col-span-2"><Label className="text-xs">Project Name *</Label><Input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} data-testid="proj-name-input" className="mt-1" /></div>
            <PmMultiSelect />
            <LaborManagerSelect />
            <div><Label className="text-xs">Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="proj-start-input" className="mt-1" /></div>
            <div><Label className="text-xs">Reporting Date</Label><Input type="date" value={form.reporting_date} onChange={(e) => setForm({ ...form, reporting_date: e.target.value })} data-testid="proj-reporting-input" className="mt-1" /></div>
            <div className="col-span-2">
              <Label className="text-xs flex items-center gap-1"><Plug className="w-3 h-3 text-[#E8791A]" /> Linked Procore Project</Label>
              {procoreLoading ? (
                <div className="mt-1 flex items-center gap-2 text-sm text-slate-500" data-testid="procore-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading Procore projects…</div>
              ) : (procoreProjects && procoreProjects.length > 0) ? (
                <Select
                  value={form.procore_project_id ? String(form.procore_project_id) : "none"}
                  onValueChange={(v) => {
                    if (v === "none") { setForm({ ...form, procore_project_id: null, procore_project_name: null }); return; }
                    const p = procoreProjects.find((x) => String(x.id) === v);
                    setForm({ ...form, procore_project_id: p.id, procore_project_name: p.name });
                  }}>
                  <SelectTrigger className="mt-1" data-testid="proj-procore-select"><SelectValue placeholder="Select a Procore project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Not linked —</SelectItem>
                    {procoreProjects.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)} data-testid={`procore-project-option-${p.id}`}>
                        {p.name}{p.project_number ? ` (#${p.project_number})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : procoreError ? (
                <div className="mt-1 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700" data-testid="procore-error">Couldn't load Procore projects: {procoreError}</div>
              ) : (procoreStatus && procoreStatus.connected && procoreStatus.company_id) ? (
                <div className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800" data-testid="procore-empty-connected">Connected, but Procore returned 0 projects.</div>
              ) : (
                <div className="mt-1 text-xs text-slate-400" data-testid="procore-not-connected">No Procore projects available. Connect Procore in Settings.</div>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4"><Button onClick={save} disabled={saving || !form.project_number || !form.project_name} className="bg-[#0B192C]" data-testid="proj-save-button">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editingId ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-md p-4 shadow-sm hover:border-[#E8791A] transition-colors" data-testid={`project-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="cursor-pointer" onClick={() => select(p.id)}>
                <div className="font-mono text-xs text-slate-500">#{p.project_number}</div>
                <div className="font-head font-semibold text-lg text-slate-900">{p.project_name}</div>
              </div>
              {p.archived && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">Archived</span>}
            </div>
            <div className="text-sm text-slate-500 mt-2" data-testid={`project-pms-${p.id}`}>
              PM: {(p.manager_ids && p.manager_ids.length) ? p.manager_ids.map(pmName).join(", ") : (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid={`project-needs-pm-${p.id}`}>Needs a PM</span>
              )}
            </div>
            <div className="text-sm text-slate-500 mt-1" data-testid={`project-lm-${p.id}`}>
              Labor Managers: {(p.labor_manager_ids && p.labor_manager_ids.length) ? p.labor_manager_ids.map(pmName).join(", ") : (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid={`project-needs-lm-${p.id}`}>Needs a Labor Manager</span>
              )}
            </div>
            <div className="text-xs text-slate-400 mt-1">Reporting: {p.reporting_date || "—"}</div>
            {p.procore_last_synced_at && (
              <div className="text-[11px] text-slate-400 mt-1" data-testid={`project-last-synced-${p.id}`}>Procore synced: {new Date(p.procore_last_synced_at).toLocaleString()}</div>
            )}
            <div className="mt-2" data-testid={`project-procore-link-${p.id}`}>
              {p.procore_project_id ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#E8791A] bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">
                  <Plug className="w-3 h-3" /> Procore: {p.procore_project_name || `#${p.procore_project_id}`}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><Plug className="w-3 h-3" /> Not linked to Procore</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => select(p.id)} data-testid={`select-project-${p.id}`} className="hover:bg-slate-900 hover:text-white"><CheckCircle2 className="w-4 h-4 mr-1" />Select</Button>
              {can.manage && (
                <Button size="sm" variant="outline" onClick={() => syncProcore(p.id)} disabled={syncing === p.id || !!syncAll} data-testid={`sync-project-${p.id}`} className="hover:bg-[#E8791A] hover:text-white hover:border-[#E8791A]">
                  {syncing === p.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Sync data
                </Button>
              )}
              {can.admin && (
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-project-${p.id}`} className="hover:bg-slate-900 hover:text-white"><Pencil className="w-4 h-4" /></Button>
              )}
              {can.manage && (
                <Button size="sm" variant="outline" onClick={() => window.open(`${API}/projects/${p.id}/export/backup.json`, "_blank")} data-testid={`backup-project-${p.id}`} className="hover:bg-slate-900 hover:text-white"><Download className="w-4 h-4" /></Button>
              )}
              {can.admin && (
                <Button size="sm" variant="outline" onClick={() => archive(p.id)} data-testid={`archive-project-${p.id}`} className="hover:bg-slate-900 hover:text-white ml-auto"><Archive className="w-4 h-4" /></Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
